<template>

    <div class="site-threshold-metrics-container">

        <div class="getting-data-container" v-if="showLoading">
            <v-icon x-large dark class="site-image-spinner">fas fa-spinner fa-spin</v-icon>
        </div>

        <div id="ag-grid-map-info-site-threshold-metrics-container">
            <ag-grid-vue style="width: 100%; height: 100%;" class="ag-theme-balham" v-if="showGrid"
                :gridOptions="gridOptions"
                :groupHeaders="true"
                :rowDeselection="true"
                :rowSelection="'multiple'"
                overlayNoRowsTemplate="<span>No Metrics Available</span>"
                @filterChanged="filterChanged"
                :rowHeight="36">
            </ag-grid-vue>
        </div>
    </div>
    
</template>

<script>
import _ from 'lodash';
import { AgGridVue } from "ag-grid-vue";
import { alarmDateFilterParams } from "@/components/Alarms/AlarmFuncs";
export default {
    name: 'MapItemSiteThresholdMetrics',
    props: ['infoWindowData', 'gettingSiteThresholdMetrics'],
    components: {
        'ag-grid-vue': AgGridVue,
    },
    data: () => ({
        showGrid: false,
        gridData: null,
        timeHeaderName: 'Time',
        gridApi: null,
        gridOptions: {},
    }),

    computed: {
        showLoading() {
            return this.$props.gettingSiteThresholdMetrics;
        }
    },

    methods: {
        buildAgGrid: function() {
            console.log('building metrics grid');
            this.showGrid = false;
            this.gridOptions = {};
            this.gridOptions.rowData = this.gridData;
            this.gridOptions.columnDefs = this.createColumnDefs();
            this.gridOptions.getMainMenuItems = this.getMainMenuItems;

            let self = this;
            this.gridOptions.onGridReady = function(params) {
                self.gridOptions.api.hideOverlay();
                self.gridOptions.api.sizeColumnsToFit();
                self.gridApi = self.gridOptions.api;
                if(!self.gridData.length) {
                    self.gridApi.showNoRowsOverlay();
                }
                if(self.$props.infoWindowData.siteThresholdMetricsFilters) {
                    self.gridApi.setFilterModel(self.$props.infoWindowData.siteThresholdMetricsFilters);
                }
                self.filterChanged();
            }
            this.gridOptions.defaultColDef = {
                sortable: true,
                filter: true,
                resizable: true
            }

            this.showGrid = true;
        },

        createColumnDefs: function() {
            let columnDefs = [];
            const self = this;
            columnDefs = [
                { 
                    headerName: self.timeHeaderName,
                    suppressSizeToFit: true,
                    field: 'start_time',
                    filter: 'agDateColumnFilter',
                    filterParams: alarmDateFilterParams,
                    width: 150, 
                    cellStyle: {'padding-top': '4px', 'text-align': 'left'} 
                },
                { 
                    headerName: 'Sector',
                    suppressSizeToFit: true,
                    field: 'sect_mscid', 
                    width: 90, filter: 'agSetColumnFilter', cellStyle: {'padding-top': '4px', 'text-align': 'left'}
                },
                { 
                    headerName: 'Metric',
                    suppressSizeToFit: false,
                    field: 'metric_name', 
                    width: 120, filter: 'agSetColumnFilter', cellStyle: {'padding-top': '4px', 'text-align': 'left'} 
                },
                { 
                    headerName: 'Value',
                    suppressSizeToFit: false,
                    field: 'metric_value', 
                    width: 120, filter: 'number', cellStyle: {'padding-top': '4px', 'text-align': 'left'} 
                },
            ];
            return columnDefs;
        },

        getMainMenuItems: function(params) {
            const self = this;
            if(params.column.getId() == 'start_time') {
                const timeMenuItems = params.defaultItems.slice(0);
                timeMenuItems.push(
                    'separator',
                    {
                        name: 'Group By Hour',
                        action: () => {
                            this.groupDataBy('hour');
                        },
                        checked: (self.timeHeaderName == 'Time (Hour)')
                    },
                    {
                        name: 'Group By Day',
                        action: () => {
                            this.groupDataBy('day');
                        },
                        checked: (self.timeHeaderName == 'Time (Day)')
                    },
                    {
                        name: 'No Group(Interval)',
                        action: () => {
                            this.groupDataBy(null);
                        },
                        checked: (self.timeHeaderName == 'Time')
                    },
                );
                return timeMenuItems;
            }
            else {
                return params.defaultItems;
            }
        },

        groupDataBy: function(groupByType) {
            if(!groupByType) {
                this.gridData = this.$props.infoWindowData.siteThresholdMetrics;
                this.timeHeaderName = 'Time';
            } 
            else {
                let data = this.$props.infoWindowData.siteThresholdMetrics;
                let aggDataList = [];
                let sectorMetrics = _.groupBy(data, d => `${d.sect_mscid}*|*${d.metric_name}`)

                for(let [sectorMetricsName, sectorMetric] of Object.entries(sectorMetrics)) {
                    let aggFunction = this.getAggFunction(sectorMetric[0].metric_name);
                    let groupMetrics = {};
                    if(groupByType == 'hour') {
                        groupMetrics = _.groupBy(sectorMetric, d => `${d.start_time.substr(0, 13)}`);
                        this.timeHeaderName = 'Time (Hour)';
                    }
                    else if(groupByType == 'day') {
                        groupMetrics = _.groupBy(sectorMetric, d => `${d.start_time.substr(0, 10)}`);
                        this.timeHeaderName = 'Time (Day)';
                    }

                    for(let [groupMetricName, groupMetric] of Object.entries(groupMetrics)) {
                        let value = null;
                        switch(aggFunction) {
                            case 'Min':
                                value = _.minBy(groupMetric, m => m.metric_value);
                                break;
                            case 'Max':
                                value = _.maxBy(groupMetric, m => m.metric_value);
                                break;
                            case 'Average':
                                value = _.meanBy(groupMetric, m => m.metric_value);
                                break;
                            case 'Sum':
                            default:
                                value = _.sumBy(groupMetric, m => m.metric_value);
                        }

                        aggDataList.push({
                            metric_name: sectorMetric[0].metric_name,
                            sect_mscid: sectorMetric[0].sect_mscid,
                            site_location: sectorMetric[0].site_location,
                            site_office: sectorMetric[0].site_office,
                            start_time: `${groupMetricName} (${aggFunction})`,
                            metric_value: value
                        });
                    }
                }
                this.gridData = aggDataList;
            }

            this.$props.infoWindowData.siteThresholdMetricsGroupByType = groupByType;
            this.showGrid = false;
            this.$nextTick(() => {
                this.buildAgGrid();
            });
        },

        getAggFunction(metricName) {
            let metricNameLowerParts = metricName.toLowerCase().split(' ');
            if(metricNameLowerParts.includes('avg') || metricNameLowerParts.includes('average') || metricNameLowerParts.includes('%')) {
                return 'Average';
            }
            else if(metricNameLowerParts.includes('min') || metricNameLowerParts.includes('minimum')) {
                return 'Min';
            }
            else if(metricNameLowerParts.includes('max') || metricNameLowerParts.includes('maximum')) {
                return 'Max';
            }
            else {
                return 'Sum';
            }
        },

        filterChanged: function(e) {
            let rows = [];
            this.gridApi.forEachNodeAfterFilter(r => {
                rows.push(r.data);
            });
            this.$props.infoWindowData.siteThresholdMetricsFilters = this.gridApi.getFilterModel();
            this.$props.infoWindowData.filteredSiteThresholdMetrics = rows;
        }

    },

    watch: {
        gettingSiteThresholdMetrics: {
            handler(newVal, oldVal) {
                if(newVal) {
                    this.showGrid = false;
                }
                else {
                    this.gridData = this.$props.infoWindowData.siteThresholdMetrics;
                    this.groupDataBy(this.$props.infoWindowData.siteThresholdMetricsGroupByType); // will call buildAgGrid
                }
            }
        }
    },

    mounted() {
        if(!this.$props.gettingSiteThresholdMetrics) {
            this.gridData = this.$props.infoWindowData.siteThresholdMetrics;
            this.groupDataBy(this.$props.infoWindowData.siteThresholdMetricsGroupByType); // will call buildAgGrid
        }
    }  
};
</script>

<style scoped>

    .site-threshold-metrics-container {
        height: 100%;
    }

    #ag-grid-map-info-site-threshold-metrics-container {
        height: 100%;
    }

    .getting-data-container {
        width: 100%;
    }
    .getting-data-container i {
        color: #000000;
        position: absolute;
        z-index: 2;
        left: calc(50% - (40px / 2));
        margin-top: 110px;
    }

</style>
